import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "saveBar", "saveButton", "statusMessage", "toggleButton"]

  connect() {
    this.editMode = false
    this.changes = new Map()

    this.beforeUnloadHandler = (e) => {
      if (this.changes.size > 0) {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", this.beforeUnloadHandler)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnloadHandler)
  }

  toggleEditMode() {
    this.editMode = !this.editMode

    this.rowTargets.forEach(row => {
      row.querySelectorAll("[data-display]").forEach(el => el.classList.toggle("hidden", this.editMode))
      row.querySelectorAll("[data-input]").forEach(el => el.classList.toggle("hidden", !this.editMode))
    })

    this.saveBarTarget.classList.toggle("hidden", !this.editMode)

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.textContent = this.editMode ? "Cancel Editing" : "Edit Mode"
      if (this.editMode) {
        this.toggleButtonTarget.classList.remove("bg-yellow-100", "text-yellow-700", "hover:bg-yellow-200")
        this.toggleButtonTarget.classList.add("bg-red-100", "text-red-700", "hover:bg-red-200")
      } else {
        this.toggleButtonTarget.classList.remove("bg-red-100", "text-red-700", "hover:bg-red-200")
        this.toggleButtonTarget.classList.add("bg-yellow-100", "text-yellow-700", "hover:bg-yellow-200")
      }
    }

    if (!this.editMode) {
      this.resetAllInputs()
      this.changes.clear()
      this.updateChangeCount()
      this.statusMessageTarget.textContent = ""
    }
  }

  fieldChanged(event) {
    const input = event.target
    const row = input.closest("[data-lender-batch-edit-target='row']")
    const lenderId = row.dataset.lenderId
    const field = input.dataset.field
    const originalValue = input.dataset.originalValue
    const newValue = input.value

    if (!this.changes.has(lenderId)) {
      this.changes.set(lenderId, {})
    }

    if (newValue === originalValue) {
      delete this.changes.get(lenderId)[field]
      if (Object.keys(this.changes.get(lenderId)).length === 0) {
        this.changes.delete(lenderId)
      }
      input.closest("td")?.classList.remove("bg-yellow-50")
    } else {
      this.changes.get(lenderId)[field] = newValue
      input.closest("td")?.classList.add("bg-yellow-50")
    }

    this.updateChangeCount()
  }

  async saveAll() {
    if (this.changes.size === 0) return

    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Saving..."
    this.statusMessageTarget.textContent = ""
    this.statusMessageTarget.className = "text-sm ml-3"

    const lenders = []
    this.changes.forEach((fields, lenderId) => {
      const payload = { id: lenderId }
      Object.entries(fields).forEach(([field, value]) => {
        if (field === "secured_composite") {
          if (value === "unsecured") {
            payload.unsecured = true
            payload.lien_position = null
          } else if (value === "secured_0") {
            payload.unsecured = false
            payload.lien_position = 0
          } else if (value === "secured_1") {
            payload.unsecured = false
            payload.lien_position = 1
          }
        } else {
          payload[field] = value
        }
      })
      lenders.push(payload)
    })

    try {
      const url = this.element.dataset.batchUpdateUrl
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta ? csrfMeta.content : ""

      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ lenders: lenders })
      })

      const contentType = response.headers.get("content-type") || ""
      if (!contentType.includes("application/json")) {
        this.statusMessageTarget.textContent = `Server error (${response.status}). Try refreshing the page.`
        this.statusMessageTarget.classList.add("text-red-700")
        return
      }

      const data = await response.json()

      if (response.ok) {
        this.statusMessageTarget.textContent = `Updated ${data.updated.length} lender(s) successfully.`
        this.statusMessageTarget.classList.add("text-green-700")
        this.commitChanges()
        this.changes.clear()
        this.updateChangeCount()
      } else {
        const successCount = data.updated?.length || 0
        const errorCount = data.errors?.length || 0
        this.statusMessageTarget.textContent = `${successCount} updated, ${errorCount} failed.`
        this.statusMessageTarget.classList.add("text-red-700")
        this.markErrorRows(data.errors)
        if (data.updated) {
          data.updated.forEach(item => {
            this.changes.delete(String(item.id))
            const row = this.element.querySelector(`[data-lender-id="${item.id}"]`)
            if (row) {
              row.querySelectorAll("td.bg-yellow-50").forEach(td => td.classList.remove("bg-yellow-50"))
            }
          })
        }
        this.updateChangeCount()
      }
    } catch (error) {
      this.statusMessageTarget.textContent = "Network error. Please try again."
      this.statusMessageTarget.classList.add("text-red-700")
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.textContent = "Save All Changes"
    }
  }

  // ── Private helpers ──

  updateChangeCount() {
    const badge = this.element.querySelector("[data-change-count]")
    if (badge) {
      badge.textContent = this.changes.size > 0 ? `${this.changes.size} row(s) modified` : ""
    }
  }

  resetAllInputs() {
    this.rowTargets.forEach(row => {
      row.querySelectorAll("[data-input] select, [data-input] input").forEach(input => {
        input.value = input.dataset.originalValue || ""
      })
      row.querySelectorAll("td.bg-yellow-50").forEach(td => td.classList.remove("bg-yellow-50"))
      row.querySelectorAll(".row-error").forEach(el => el.remove())
    })
  }

  commitChanges() {
    this.rowTargets.forEach(row => {
      row.querySelectorAll("[data-input] select, [data-input] input").forEach(input => {
        input.dataset.originalValue = input.value
      })
      row.querySelectorAll("td.bg-yellow-50").forEach(td => td.classList.remove("bg-yellow-50"))
    })
  }

  markErrorRows(errors) {
    if (!errors) return
    errors.forEach(err => {
      const row = this.element.querySelector(`[data-lender-id="${err.id}"]`)
      if (row) {
        row.querySelectorAll(".row-error").forEach(el => el.remove())
        const td = row.querySelector("td:first-child")
        if (td) {
          const errorEl = document.createElement("div")
          errorEl.className = "row-error text-xs text-red-600 mt-1"
          errorEl.textContent = err.messages.join(", ")
          td.appendChild(errorEl)
        }
      }
    })
  }
}
